import React, { useEffect, useMemo, useRef, useState } from "react";
import { formatDate, formatDateTime } from "@beleef/utils/date";
import {
  FiMapPin,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiRepeat,
  FiArrowRight,
  FiList,
  FiUser,
} from "react-icons/fi";
import Select from "react-select";
import PageLoader from "components/ui/PageLoader";
import { useEventLogsQuery } from "apis/event";

const changeTypeConfig = {
  create: {
    icon: FiPlus,
    bg: "bg-[#E8F5E9]",
    text: "text-[#2E7D32]",
    label: "Created",
  },
  update: {
    icon: FiEdit,
    bg: "bg-[#E3F2FD]",
    text: "text-[#1565C0]",
    label: "Updated",
  },
  reschedule: {
    icon: FiRepeat,
    bg: "bg-[#FFF8E1]",
    text: "text-[#F9A825]",
    label: "Rescheduled",
  },
  cancel: {
    icon: FiTrash2,
    bg: "bg-[#FFEBEE]",
    text: "text-[#C62828]",
    label: "Cancelled",
  },
};

const sourceConfig = {
  "web-app": { label: "Web", bg: "bg-[#F3E5F5]", text: "text-[#7B1FA2]" },
  "mobile-app": { label: "App", bg: "bg-[#E8EAF6]", text: "text-[#3949AB]" },
  admin: { label: "Admin", bg: "bg-[#ECEFF1]", text: "text-[#546E7A]" },
  cron: { label: "System", bg: "bg-[#FFF3E0]", text: "text-[#E65100]" },
};

const getSourceInfo = (source) => {
  if (!source)
    return { label: "Unknown", bg: "bg-lightgray", text: "text-darkergray" };
  const sourceKey = Object.keys(sourceConfig).find((key) =>
    source.toLowerCase().startsWith(key),
  );
  return (
    sourceConfig[sourceKey] || {
      label: source,
      bg: "bg-lightgray",
      text: "text-darkergray",
    }
  );
};

const PAGE_SIZE = 20;

const LogCard = ({ log }) => {
  const changeType =
    changeTypeConfig[log.changeType] || changeTypeConfig.update;
  const ChangeIcon = changeType.icon;
  const sourceInfo = getSourceInfo(log.source);

  const hasTimeChange =
    log.old?.start !== log.new?.start || log.old?.end !== log.new?.end;
  const hasContractorChange =
    log.old?.contractorId?.toString() !== log.new?.contractorId?.toString();

  const changedByName = log.changedBy?.name || "System";
  const changedByEmail = log.changedBy?.email;

  return (
    <div className="bg-white border border-mediumgray rounded-lg p-4 mb-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${changeType.bg} ${changeType.text}`}
          >
            <ChangeIcon size={11} />
            {changeType.label}
          </span>
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${sourceInfo.bg} ${sourceInfo.text}`}
          >
            {sourceInfo.label}
          </span>
        </div>
        <span className="text-xs text-darkergray">
          {formatDateTime(log.createdAt)}
        </span>
      </div>

      <p className="font-medium text-black mb-1">{log.eventSummary}</p>
      <div className="flex items-center gap-1 text-sm text-darkergray mb-2">
        <FiMapPin size={14} />
        <span>{log.address}</span>
      </div>

      <div className="flex items-center gap-1 text-xs text-darkergray mb-3">
        <FiUser size={14} />
        <span>
          {changedByName}
          {changedByEmail && (
            <span className="text-darkergray"> · {changedByEmail}</span>
          )}
        </span>
      </div>

      <div className="bg-lightgray rounded p-3 space-y-2">
        {log.changeType === "create" && (
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
            <div>
              <span className="text-darkergray">Date: </span>
              <span className="text-black">{formatDate(log.new?.start)}</span>
            </div>
            <div>
              <span className="text-darkergray">Contractor: </span>
              <span className="text-black">
                {log.new?.contractorName || "-"}
              </span>
            </div>
          </div>
        )}

        {log.changeType === "cancel" && (
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
            <div>
              <span className="text-darkergray">Was scheduled: </span>
              <span className="text-black">{formatDate(log.old?.start)}</span>
            </div>
            <div>
              <span className="text-darkergray">Contractor: </span>
              <span className="text-black">
                {log.old?.contractorName || "-"}
              </span>
            </div>
          </div>
        )}

        {(log.changeType === "update" || log.changeType === "reschedule") && (
          <>
            {hasTimeChange && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-darkergray">Time:</span>
                <span className="text-darkergray">
                  {formatDateTime(log.old?.start)}
                </span>
                <FiArrowRight size={11} className="text-darkergray" />
                <span className="text-black font-medium">
                  {formatDateTime(log.new?.start)}
                </span>
              </div>
            )}
            {hasContractorChange && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-darkergray">Contractor:</span>
                <span className="text-darkergray">
                  {log.old?.contractorName || "-"}
                </span>
                <FiArrowRight size={11} className="text-darkergray" />
                <span className="text-black font-medium">
                  {log.new?.contractorName || "-"}
                </span>
              </div>
            )}
            {!hasTimeChange && !hasContractorChange && (
              <p className="text-sm text-darkergray">
                No major changes recorded
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const EventLogs = ({ properties }) => {
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedChangeType, setSelectedChangeType] = useState(null);
  const [page, setPage] = useState(1);
  const [pendingScroll, setPendingScroll] = useState(false);

  const listTopRef = useRef(null);

  const propertyId =
    selectedProperty?.value && selectedProperty.value !== "all"
      ? selectedProperty.value
      : undefined;

  const changeType =
    selectedChangeType?.value && selectedChangeType.value !== "all"
      ? selectedChangeType.value
      : undefined;

  // Filter changes go back to page 1 and trigger a scroll to top of list.
  const handlePropertyChange = (val) => {
    setSelectedProperty(val);
    setPage(1);
    setPendingScroll(true);
  };
  const handleChangeTypeChange = (val) => {
    setSelectedChangeType(val);
    setPage(1);
    setPendingScroll(true);
  };

  // Pagination handlers also flag a pending scroll. The actual scroll fires
  // in the effect below once the new page's data has arrived AND been painted.
  const handleNextPage = () => {
    setPage((p) => p + 1);
    setPendingScroll(true);
  };
  const handlePrevPage = () => {
    setPage((p) => Math.max(1, p - 1));
    setPendingScroll(true);
  };

  const { data, isLoading, isFetching } = useEventLogsQuery({
    propertyId,
    changeType,
    page,
  });

  const logs = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Scroll once the new page is fetched AND rendered. Two requestAnimationFrames
  // guarantee we're past React's commit + browser paint, so listTopRef is in
  // its final post-render position before we measure it.
  useEffect(() => {
    if (!pendingScroll) return;
    if (isFetching) return;
    if (!data) return;

    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        listTopRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        setPendingScroll(false);
      });
      // Cleanup reference for inner rAF in case the outer is cancelled
      return () => cancelAnimationFrame(raf2);
    });

    return () => cancelAnimationFrame(raf1);
  }, [pendingScroll, isFetching, data]);

  const stats = useMemo(
    () => ({
      create: logs.filter((l) => l.changeType === "create").length,
      update: logs.filter((l) => l.changeType === "update").length,
      reschedule: logs.filter((l) => l.changeType === "reschedule").length,
      cancel: logs.filter((l) => l.changeType === "cancel").length,
    }),
    [logs],
  );

  const formattedProperties = useMemo(
    () => [
      { label: "All Properties", value: "all" },
      ...properties.map((p) => ({ label: p.address, value: p.propertyId })),
    ],
    [properties],
  );

  const changeTypeOptions = [
    { label: "All Types", value: "all" },
    { label: "Created", value: "create" },
    { label: "Updated", value: "update" },
    { label: "Rescheduled", value: "reschedule" },
    { label: "Cancelled", value: "cancel" },
  ];

  if (isLoading && !data) return <PageLoader />;

  return (
    <div>
      <div className="grid grid-cols-4 gap-3 mb-4">
        {Object.entries(changeTypeConfig).map(([key, cfg]) => (
          <div key={key} className={`${cfg.bg} rounded-lg p-3 text-center`}>
            <p className={`text-xl font-bold ${cfg.text}`}>{stats[key]}</p>
            <p className={`text-xs ${cfg.text}`}>{cfg.label}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-3 mb-4">
        <Select
          options={formattedProperties}
          value={selectedProperty}
          onChange={handlePropertyChange}
          placeholder="Filter by property..."
          isClearable
          className="text-sm flex-1 max-w-xs"
        />
        <Select
          options={changeTypeOptions}
          value={selectedChangeType}
          onChange={handleChangeTypeChange}
          placeholder="Filter by type..."
          isClearable
          className="text-sm w-40"
        />
      </div>

      {logs.length === 0 ? (
        <div className="text-center py-12 text-darkergray">
          <FiList size={32} className="mx-auto mb-3 text-mediumgray" />
          <p>No logs found</p>
        </div>
      ) : (
        <>
          <div ref={listTopRef} />

          <div className={isFetching ? "opacity-60 transition-opacity" : ""}>
            {logs.map((log) => (
              <LogCard key={log._id} log={log} />
            ))}
          </div>

          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-darkergray">
              Page {page} of {totalPages} · {total.toLocaleString()} total
            </p>
            <div className="flex gap-2">
              <button
                onClick={handlePrevPage}
                disabled={page === 1 || isFetching}
                className="outline-button px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={handleNextPage}
                disabled={page >= totalPages || isFetching}
                className="outline-button px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EventLogs;
